import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  onSnapshot
} from 'firebase/firestore';

// Definimos la estructura del documento de configuración
interface BuildConfig {
  buildNumber: number;
  lastUpdate: Date;
  forceUpdate?: boolean;
}

export class BuildVersionService {
  private static instance: BuildVersionService;
  private db = FirebaseProvider.getInstance().getFirestore();
  private configCollectionPath = 'Config';
  private buildDocId = 'build';

  private constructor() {}

  public static getInstance(): BuildVersionService {
    if (!BuildVersionService.instance) {
      BuildVersionService.instance = new BuildVersionService();
    }
    return BuildVersionService.instance;
  }

  /**
   * Obtiene el número de build actual desde Firestore
   */
  public async getCurrentBuildNumber(): Promise<number> {
    try {
      const configRef = doc(this.db, this.configCollectionPath, this.buildDocId);
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const data = configDoc.data() as BuildConfig;
        return data.buildNumber;
      } else {
        // Si no existe el documento, lo creamos con el build número 1
        const initialBuild: BuildConfig = {
          buildNumber: 1,
          lastUpdate: new Date()
        };
        await setDoc(configRef, initialBuild);
        return 1;
      }
    } catch (error) {
      console.error('Error al obtener el número de build:', error);
      return 0; // Devolvemos 0 en caso de error
    }
  }

  /**
   * Incrementa el número de build en Firestore
   */
  public async incrementBuildNumber(): Promise<number> {
    try {
      const currentBuild = await this.getCurrentBuildNumber();
      const newBuildNumber = currentBuild + 1;
      
      const configRef = doc(this.db, this.configCollectionPath, this.buildDocId);
      await updateDoc(configRef, {
        buildNumber: newBuildNumber,
        lastUpdate: new Date()
      });
      
      return newBuildNumber;
    } catch (error) {
      console.error('Error al incrementar el número de build:', error);
      throw error;
    }
  }

  /**
   * Configura un listener para cambios en el número de build
   * @param callback Función a ejecutar cuando cambie el número de build
   */
  public subscribeToBuildChanges(callback: (buildNumber: number) => void): () => void {
    const configRef = doc(this.db, this.configCollectionPath, this.buildDocId);
    
    const unsubscribe = onSnapshot(configRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as BuildConfig;
        callback(data.buildNumber);
      }
    }, (error) => {
      console.error('Error al escuchar cambios de build:', error);
    });
    
    return unsubscribe;
  }
} 